import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../../lib/hooks';
import { MIcon } from '../../lib/micon';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdTextfeld, LdUmschalter, LdFensterFuss, datumLang } from '../../components/ld/LdFenster';
import { KkMenu } from '../klasse/KlassenKopf';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { ApiError } from '../../lib/api';
import { openAusflugDetail, oeffneTerminBearbeiten } from '../../lib/nav';
import { schuljahrFenster } from '../../lib/schuljahr';
import {
  ausflugApi, AusflugDetailResponse, AusflugTeilnehmer,
  Zustimmung, Zahlung,
} from './ausflug-api';
import { AnwesenheitErfassen } from './AnwesenheitErfassen';
import { MahnungModal } from './MahnungModal';
import { TeilnehmerVerwaltenModal } from './TeilnehmerVerwaltenModal';
import '../../components/diktat/diktat-review.css';
import './ausflug.css';

// ── Welle K4: kompakte Teilnehmer-Liste ───────────────────────────────────────
type FilterKey = 'offen' | 'alle' | 'mit_notiz';

function matchtFilter(t: AusflugTeilnehmer, f: FilterKey): boolean {
  switch (f) {
    case 'offen':     return t.zustimmung === 'offen' || t.zahlung === 'offen';
    case 'mit_notiz': return !!t.notiz && t.notiz.trim() !== '';
    default:          return true;
  }
}

const euro = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;
const ZU_TEXT: Record<Zustimmung, string> = { zugestimmt: 'ja', offen: 'offen', abgelehnt: 'abgelehnt' };
const ZA_TEXT: Record<Zahlung, string> = { bezahlt: 'bezahlt', offen: 'offen', befreit: 'befreit' };
const ton = (w: string) => (w === 'zugestimmt' || w === 'bezahlt' ? 'gruen' : w === 'offen' ? 'offen' : 'grau');

// Überblick: Balken in Primärfarbe, offen in Amber
function Balken({ titel, erfuellt, gesamt, offen, extra }: { titel: string; erfuellt: number; gesamt: number; offen: number; extra?: string }) {
  const pct = gesamt > 0 ? Math.round((erfuellt / gesamt) * 100) : 0;
  return (
    <div className="ld-af-balken">
      <div className="ld-af-balken__kopf"><b>{titel}</b><span>{erfuellt} von {gesamt}</span></div>
      <div className="ld-af-balken__spur" role="img" aria-label={`${titel}: ${erfuellt} von ${gesamt}`}><i style={{ width: `${pct}%` }} /></div>
      <div className="ld-af-balken__fuss">
        {offen > 0 ? <span className="ist-offen">{offen} offen</span> : <span>nichts offen</span>}
        {extra && <span>{extra}</span>}
      </div>
    </div>
  );
}

// Fenster je Kind: Zustimmung, Zahlung, Notiz
function KindFenster({ t, titel, onClose, onSpeichern }: {
  t: AusflugTeilnehmer; titel: string; onClose: () => void;
  onSpeichern: (patch: { zustimmung?: Zustimmung; zahlung?: Zahlung; notiz?: string | null }) => void;
}) {
  const [zu, setZu] = useState<Zustimmung>(t.zustimmung);
  const [za, setZa] = useState<Zahlung>(t.zahlung);
  const [notiz, setNotiz] = useState(t.notiz ?? '');
  function speichern() {
    const patch: { zustimmung?: Zustimmung; zahlung?: Zahlung; notiz?: string | null } = {};
    if (zu !== t.zustimmung) patch.zustimmung = zu;
    if (za !== t.zahlung) patch.zahlung = za;
    if (notiz.trim() !== (t.notiz ?? '').trim()) patch.notiz = notiz.trim() || null;
    if (Object.keys(patch).length) onSpeichern(patch);
    onClose();
  }
  return (
    <LdSheet open onClose={onClose} maxWidth={480}
      kopf={{ titel: `${t.vorname} ${t.nachname}`, kontext: `${titel}${t.mahnung_anzahl > 0 ? `, ${t.mahnung_anzahl}× erinnert` : ''}` }}
      fuss={<LdFensterFuss onAbbrechen={onClose} haupt="Speichern" onHaupt={speichern} />}>
      <LdFeld label="Zustimmung">
        <LdUmschalter aria-label="Zustimmung" wert={zu} onChange={setZu}
          optionen={[{ wert: 'zugestimmt', label: 'Zugestimmt', ton: 'gruen' }, { wert: 'abgelehnt', label: 'Abgelehnt' }, { wert: 'offen', label: 'Offen', ton: 'amber' }]} />
      </LdFeld>
      <LdFeld label="Zahlung">
        <LdUmschalter aria-label="Zahlung" wert={za} onChange={setZa}
          optionen={[{ wert: 'bezahlt', label: 'Bezahlt', ton: 'gruen' }, { wert: 'befreit', label: 'Befreit' }, { wert: 'offen', label: 'Offen', ton: 'amber' }]} />
      </LdFeld>
      <LdFeld label="Notiz" optional htmlFor="ld-af-notiz">
        <LdTextfeld id="ld-af-notiz" rows={2} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z. B. Allergie beachten" />
      </LdFeld>
    </LdSheet>
  );
}

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
export function AusflugDetail({ terminId, onBack }: { terminId: number | null; onBack: () => void }) {
  const isMobile = useIsMobile();
  const [data, setData]     = useState<AusflugDetailResponse | null>(null);
  const [laden, setLaden]   = useState(true);
  const [filter, setFilter] = useState<FilterKey | null>(null);
  const [view, setView]     = useState<'liste' | 'anwesenheit'>('liste');
  const [erinnern, setErinnern] = useState(false);
  const [kindOffen, setKindOffen] = useState<AusflugTeilnehmer | null>(null);
  const [teilnehmerVerwalten, setTeilnehmerVerwalten] = useState(false);
  const [vorjahrBestaetigt, setVorjahrBestaetigt] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);   // U5: dezente Meldung (z.B. 409 beim Löschen)
  const [aktionLaeuft, setAktionLaeuft] = useState(false);

  const lade = useCallback(async () => {
    if (terminId == null) return;
    try {
      const d = await ausflugApi.laden(terminId);
      setData(d);
    } catch (err: any) {
      toast(err.message ?? 'Ausflug konnte nicht geladen werden.', 'error');
    } finally {
      setLaden(false);
    }
  }, [terminId]);

  useEffect(() => { setLaden(true); lade(); }, [lade]);

  // Optimistisches Teilnehmer-Update mit Rollback
  async function patchTeilnehmer(schuelerId: number, patch: { zustimmung?: Zustimmung; zahlung?: Zahlung; notiz?: string | null }) {
    if (!data || terminId == null) return;
    const vorher = data;
    setData({
      ...data,
      teilnehmer: data.teilnehmer.map(t => t.schueler_id === schuelerId ? { ...t, ...patch } : t),
    });
    try {
      await ausflugApi.patchTeilnehmer(terminId, schuelerId, patch);
      lade(); // Stats neu berechnen
    } catch (err: any) {
      toast(err.message ?? 'Speichern fehlgeschlagen.', 'error');
      setData(vorher); // Rollback
    }
  }

  if (laden) {
    return <div className="ld-page-enter ld-af"><p className="ld-fe-text" role="status">Lädt …</p></div>;
  }
  if (!data) {
    return (
      <div className="ld-page-enter ld-af">
        <button type="button" className="ld-kl-link ld-af__zurueck" onClick={onBack}><MIcon name="arrow_back" size={18} />Zurück</button>
        <p className="ld-fe-text">Ausflug nicht gefunden.</p>
      </div>
    );
  }

  const { termin, stats } = data;

  // Sub-Modus: Anwesenheit erfassen
  if (view === 'anwesenheit') {
    return (
      <AnwesenheitErfassen
        termin={termin}
        teilnehmer={data.teilnehmer}
        bestaetigtVorjahr={vorjahrBestaetigt}
        onBack={() => setView('liste')}
        onDone={() => { setView('liste'); lade(); }}
      />
    );
  }

  const anzahl = (f: FilterKey) => data.teilnehmer.filter(t => matchtFilter(t, f)).length;
  // Standard: „Offen“, solange etwas offen ist
  const aktFilter: FilterKey = filter ?? (anzahl('offen') > 0 ? 'offen' : 'alle');
  const sichtbar = data.teilnehmer.filter(t => matchtFilter(t, aktFilter))
    .sort((a, b) => a.nachname.localeCompare(b.nachname, 'de') || a.vorname.localeCompare(b.vorname, 'de'));
  const filterPills: { key: FilterKey; label: string }[] = [
    { key: 'offen', label: `Offen ${anzahl('offen')}` },
    { key: 'alle', label: `Alle ${stats.gesamt}` },
    { key: 'mit_notiz', label: `Mit Notiz ${anzahl('mit_notiz')}` },
  ];

  const anwesenheitErfasst = stats.anwesend_erfasst > 0;

  // ── Welle U5: Abschließen / Wieder öffnen / Löschen / Bearbeiten ──
  const abgeschlossen = !!termin.abgeschlossen_am;
  const ausVorjahr = termin.datum.slice(0, 10) < schuljahrFenster().von;
  const heute = new Date().toLocaleDateString('sv-SE');
  const tag = termin.datum.slice(0, 10);
  const vorDemTag = tag > heute;

  async function abschliessenUmschalten() {
    if (!abgeschlossen && !(await confirm('Es werden keine Fehlzeiten angelegt.', { title: 'Ausflug abschließen?', confirmLabel: 'Abschließen' }))) return;
    setAktionLaeuft(true);
    try {
      if (abgeschlossen) await ausflugApi.oeffnen(termin.id); else await ausflugApi.abschliessen(termin.id);
      toast(abgeschlossen ? 'Ausflug wieder geöffnet' : 'Ausflug abgeschlossen', 'success');
      await lade();
    } catch (err: any) { toast(err.message ?? 'Speichern fehlgeschlagen.', 'error'); }
    finally { setAktionLaeuft(false); }
  }

  async function loeschen() {
    if (!(await confirm(`„${termin.titel}“ mit allen Teilnehmern löschen?`, { title: 'Ausflug löschen?', confirmLabel: 'Löschen', danger: true }))) return;
    setAktionLaeuft(true);
    try {
      await ausflugApi.loeschen(termin.id);
      toast('Ausflug gelöscht', 'success');
      onBack();
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 409) setHinweis(err.message);
      else toast(err.message ?? 'Löschen fehlgeschlagen.', 'error');
    } finally { setAktionLaeuft(false); }
  }

  async function anwesenheitOeffnen() {
    if (ausVorjahr) {
      const ok = await confirm('Dieser Ausflug liegt im vorigen Schuljahr. Für Abwesende werden rückwirkend unentschuldigte Fehlzeiten angelegt.', {
        title: 'Anwesenheit nachtragen?', confirmLabel: 'Trotzdem erfassen',
      });
      if (!ok) return;
      setVorjahrBestaetigt(true);
    }
    setView('anwesenheit');
  }

  const kontext = [
    `${datumLang(termin.datum)}${termin.datum_ende && termin.datum_ende.slice(0, 10) !== tag ? ` bis ${datumLang(termin.datum_ende)}` : ''}`,
    termin.klasse_name ? `Klasse ${termin.klasse_name}` : null,
    termin.ausflug_kosten != null ? `${euro(termin.ausflug_kosten)} pro Kind` : null,
  ].filter(Boolean).join(', ');
  const faellig = termin.ausflug_zahlung_bis ? `fällig bis ${datumLang(termin.ausflug_zahlung_bis).replace(/\d{4}$/, '').replace(/\.$/, '.')}` : undefined;
  const nichtsZuErinnern = stats.offen_zustimmung === 0 && stats.offen_zahlung === 0;

  const ueberblick = (
    <section className="ld-kl-karte ld-af-ueber" aria-label="Überblick">
      <Balken titel="Zustimmung" erfuellt={stats.zugestimmt} gesamt={stats.gesamt} offen={stats.offen_zustimmung}
        extra={stats.abgelehnt > 0 ? `${stats.abgelehnt} abgelehnt` : undefined} />
      <Balken titel="Zahlung" erfuellt={stats.bezahlt + stats.befreit} gesamt={stats.gesamt} offen={stats.offen_zahlung} extra={faellig} />
      <div className="ld-af-ueber__anw">
        {abgeschlossen ? (
          <p><MIcon name="task_alt" size={18} />Abgeschlossen{anwesenheitErfasst ? `, ${stats.anwesend} da, ${stats.fehlt} gefehlt` : ''}</p>
        ) : anwesenheitErfasst ? (
          <p><MIcon name="how_to_reg" size={18} />Anwesenheit: {stats.anwesend} da, {stats.fehlt} gefehlt
            <button type="button" className="ld-kl-link" onClick={anwesenheitOeffnen}>Ändern</button></p>
        ) : vorDemTag ? (
          <p><MIcon name="info" size={18} />Die Anwesenheit erfasst du am Ausflugstag. Wer fehlt, bekommt eine Fehlzeit.</p>
        ) : (
          <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-af-ueber__haupt" onClick={anwesenheitOeffnen}>Anwesenheit erfassen</button>
        )}
        {!abgeschlossen && (
          <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit ld-af-ueber__zweit" disabled={nichtsZuErinnern} onClick={() => setErinnern(true)}>
            <MIcon name="mail" size={18} />Eltern erinnern
          </button>
        )}
      </div>
    </section>
  );

  return (
    <div className="ld-page-enter ld-af">
      <button type="button" className="ld-kl-link ld-af__zurueck" onClick={onBack}><MIcon name="arrow_back" size={18} />Zurück</button>
      <div className="ld-af__kopf">
        <div>
          <h1 className="ld-af__titel">{termin.titel}</h1>
          <p className="ld-af__kontext">{kontext}{abgeschlossen ? ', abgeschlossen' : ''}</p>
        </div>
        <KkMenu eintraege={[
          { label: 'Bearbeiten', icon: 'edit', onClick: () => oeffneTerminBearbeiten(termin.id, () => openAusflugDetail(termin.id, 'behalten')) },
          { label: 'Teilnehmer verwalten', icon: 'group_add', onClick: () => setTeilnehmerVerwalten(true) },
          { label: abgeschlossen ? 'Wieder öffnen' : 'Abschließen', icon: abgeschlossen ? 'lock_open' : 'task_alt', onClick: () => { if (!aktionLaeuft) abschliessenUmschalten(); } },
          { label: 'Löschen', icon: 'delete', onClick: () => { if (!aktionLaeuft) loeschen(); } },
        ]} />
      </div>
      {hinweis && (
        <div role="status" className="ld-af__hinweis">
          <span>{hinweis}</span>
          <button type="button" onClick={() => setHinweis(null)} aria-label="Hinweis schließen"><MIcon name="close" size={18} /></button>
        </div>
      )}

      <div className="ld-af__raster">
        {isMobile && ueberblick}

        <section className="ld-af__liste" aria-label="Teilnehmer">
          <div className="ld-af-filter" role="group" aria-label="Filter">
            {filterPills.map(p => (
              <button key={p.key} type="button" aria-pressed={aktFilter === p.key} className={aktFilter === p.key ? 'ist-an' : undefined} onClick={() => setFilter(p.key)}>{p.label}</button>
            ))}
          </div>

          {data.teilnehmer.length === 0 ? (
            <div className="ld-kl-karte ld-af-leer">
              <b>Noch keine Teilnehmer</b>
              <span>Über ⋯ und „Teilnehmer verwalten“ wählst du eine Klasse und fügst Kinder hinzu.</span>
            </div>
          ) : sichtbar.length === 0 ? (
            <div className="ld-kl-karte ld-af-leer"><span>{aktFilter === 'offen' ? 'Nichts mehr offen.' : 'Keine Notizen.'}</span></div>
          ) : (
            <div className="ld-kl-karte ld-af-tabelle" role="table" aria-label="Teilnehmer">
              <div className="ld-af-zeile ld-af-zeile--kopf" role="row">
                <span role="columnheader">Kind</span><span role="columnheader">Zustimmung</span><span role="columnheader">Zahlung</span>
              </div>
              {sichtbar.map(t => {
                const zweite = [t.notiz?.trim(), t.mahnung_anzahl > 0 ? `${t.mahnung_anzahl}× erinnert` : ''].filter(Boolean).join(', ');
                return (
                  <div key={t.id} className="ld-af-zeile" role="row">
                    <button type="button" role="cell" className="ld-af-zeile__kind" onClick={() => setKindOffen(t)}>
                      <b>{t.nachname}, {t.vorname}</b>
                      {zweite && <small>{zweite}</small>}
                    </button>
                    <span role="cell">
                      <button type="button" className={`ld-af-pill ton-${ton(t.zustimmung)}`}
                        aria-label={t.zustimmung === 'offen' ? `${t.vorname}: Zustimmung als ja eintragen` : `${t.vorname}: Zustimmung ${ZU_TEXT[t.zustimmung]}, ändern`}
                        onClick={() => (t.zustimmung === 'offen' ? patchTeilnehmer(t.schueler_id, { zustimmung: 'zugestimmt' }) : setKindOffen(t))}>
                        {ZU_TEXT[t.zustimmung]}
                      </button>
                    </span>
                    <span role="cell">
                      <button type="button" className={`ld-af-pill ton-${ton(t.zahlung)}`}
                        aria-label={t.zahlung === 'offen' ? `${t.vorname}: als bezahlt eintragen` : `${t.vorname}: Zahlung ${ZA_TEXT[t.zahlung]}, ändern`}
                        onClick={() => (t.zahlung === 'offen' ? patchTeilnehmer(t.schueler_id, { zahlung: 'bezahlt' }) : setKindOffen(t))}>
                        {ZA_TEXT[t.zahlung]}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {data.teilnehmer.length > 0 && <p className="ld-af__tipp">Tippe auf „offen“, um es direkt zu erledigen. Der Name öffnet alle Angaben.</p>}
        </section>

        {!isMobile && <aside className="ld-af__seite">{ueberblick}</aside>}
      </div>

      {kindOffen && (
        <KindFenster t={kindOffen} titel={termin.titel} onClose={() => setKindOffen(null)}
          onSpeichern={patch => patchTeilnehmer(kindOffen.schueler_id, patch)} />
      )}

      {erinnern && (
        <MahnungModal
          terminId={termin.id}
          termin={termin}
          typ={stats.offen_zustimmung > 0 ? 'zustimmung' : 'zahlung'}
          offen={{ zustimmung: stats.offen_zustimmung, zahlung: stats.offen_zahlung }}
          onClose={() => setErinnern(false)}
          onSent={() => { setErinnern(false); lade(); }}
        />
      )}

      {teilnehmerVerwalten && (
        <TeilnehmerVerwaltenModal
          termin={termin}
          vorhandeneIds={data.teilnehmer.map(t => t.schueler_id)}
          teilnehmer={data.teilnehmer}
          onEntfernt={() => lade()}
          onClose={() => setTeilnehmerVerwalten(false)}
          onDone={() => { setTeilnehmerVerwalten(false); lade(); }}
        />
      )}
    </div>
  );
}
